import type { Mixpanel } from "mixpanel-browser";
import { useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { useReactiveVar } from "@apollo/client/react";
import { createGenericContext } from "@portal/app/_common/lib/createGenericContext";
import { accessTokenVar } from "@portal/app/_components/apolloUtils";
import { decodeJwt, TokenInfo } from "@portal/app/_common/lib/token";
import { usePageContext } from "@portal/app/_components/context/PageContext";
import {
  isMixpanelEnvironment,
  isTrackableVisitor,
} from "@portal/app/_common/lib/isTrackableVisitor";
import { EngagementContext, PageEngagement } from "../hooks/useEngagementTracking";

const [useMixpanelContext, MixpanelContext] = createGenericContext<Mixpanel>("MixpanelContext");
export { useMixpanelContext };

// Top-level route prefixes that get 100% session-replay coverage (vs. the
// 5% global sample applied elsewhere). Add a prefix here to opt another
// route into full recording.
const FULL_SESSION_RECORDING_PATH_PREFIXES = ["/flows/"];

// The SDK's defaults end in "/" (e.g. "track/"). `record` and `flags` are read at
// runtime but missing from the SDK's typings, so this lives outside the init literal
// to sidestep the excess-property check.
const SLASH_FREE_API_ROUTES = {
  track: "track",
  engage: "engage",
  groups: "groups",
  record: "record",
  flags: "flags",
};

// New public route families must be added here; new private routes stay excluded.
// The SDK checks the same boundary before React processes navigation.
const PUBLIC_URL =
  /^https?:\/\/[^/]+\/(?:[?#].*|(?:explore|flows|school|blog|page|view|company|download|health|business-and-office|technical-and-communications|trade-and-industry)(?:[/?#].*)?)?$/;

export const MixpanelProvider = ({
  children,
  isPreview = false,
  autocaptureEnabled = true,
}: React.PropsWithChildren<{
  debug?: boolean;
  isPreview?: boolean;
  autocaptureEnabled?: boolean;
}>) => {
  // No SDK outside Mixpanel environments, so identify/alias/people calls stay silent too.
  const apiKey = isMixpanelEnvironment() ? process.env.MIXPANEL_API_KEY : undefined;
  const [mixpanelInstance, setMixpanelInstance] = useState<Mixpanel | null>(null);
  const loadedMixpanelRef = useRef<Mixpanel | null>(null);
  const queueRef = useRef<Array<{ method: string; args: unknown[] }>>([]);
  const pathname = usePathname();
  const accessToken = useReactiveVar(accessTokenVar);
  const {
    clientLocation: { country },
  } = usePageContext();
  const sessionRecordingStartedRef = useRef(false);
  const lastPageViewRef = useRef<string | null>(null);
  const previousPathRef = useRef(pathname);
  const { activeRole, customerId } = decodeJwt<TokenInfo>(accessToken) ?? ({} as TokenInfo);
  const enabled =
    autocaptureEnabled &&
    !isPreview &&
    isTrackableVisitor({ activeRole, country }) &&
    typeof window !== "undefined" &&
    PUBLIC_URL.test(window.location.href);

  useEffect(() => {
    if (!apiKey) return;

    void import("mixpanel-browser")
      .then((mod) => {
        const mp = mod.default;
        const origin = document.location.origin;
        mp.init(apiKey, {
          api_host: `${origin}/api/mixpanel`,
          // The SDK's default routes end in "/", which Next redirects (308) before the
          // rewrite proxy runs. A sendBeacon fired on page hide can't replay its body
          // after the redirect once the document is gone, so Chrome parks that request
          // for ~25s; six of them fill the per-host connection pool and every asset on
          // the next page stalls. Slash-free routes hit the proxy in one round trip.
          api_routes: SLASH_FREE_API_ROUTES,
          record_sessions_percent: 5,
          // Empty selector unmasks static text (questions, labels, headings).
          // User input remains redacted via Mixpanel's hardcoded maskAllInputs.
          record_mask_text_selector: "",
          api_payload_format: "json",
          batch_flush_interval_ms: 1000,
          // These apply before persistence, including to events queued while loading.
          // Send-time hooks are bypassed when the SDK recovers an orphaned batch.
          property_blacklist: [
            "$current_url",
            "$referrer",
            "$initial_referrer",
            "current_url_search",
            "current_page_title",
          ],
          ...(!!process.env.MIXPANEL_DEBUG && { ignore_dnt: true, debug: true }),
        });

        loadedMixpanelRef.current = mp;
        // Flush any events queued before SDK loaded
        for (const { method, args } of queueRef.current) {
          (mp as unknown as Record<string, (...a: unknown[]) => unknown>)[method](...args);
        }
        queueRef.current = [];

        setMixpanelInstance(mp);
      })
      .catch(() => {
        // Mixpanel SDK failed to load — queued events will be dropped.
        // Non-critical: analytics should not break the user experience.
        queueRef.current = [];
      });
  }, [apiKey]);

  useEffect(() => {
    if (!mixpanelInstance || !pathname) return;
    if (previousPathRef.current !== pathname) {
      lastPageViewRef.current = null;
      previousPathRef.current = pathname;
    }
    mixpanelInstance.set_config({
      autocapture: enabled
        ? {
            allow_url_regexes: [PUBLIC_URL],
            // Track page views after React commits so departed funnel IDs are cleared.
            pageview: false,
            // Answers come from Question Answered; scroll events carry no page path.
            input: false,
            scroll: false,
            capture_text_content: false,
            block_attrs: ["href", "title", "aria-label", "aria-labelledby", "aria-describedby"],
            capture_extra_attrs: [
              "data-field-key",
              "data-program-id",
              "data-provider-id",
              "data-navigation-action",
            ],
          }
        : false,
    });
    if (!enabled) {
      return;
    }
    if (customerId) mixpanelInstance.register({ customerId }, { persistent: false });
    if (lastPageViewRef.current !== pathname) {
      mixpanelInstance.track_pageview({ path: pathname });
      lastPageViewRef.current = pathname;
    }
    return () => {
      mixpanelInstance.set_config({ autocapture: false });
      if (customerId) mixpanelInstance.unregister("customerId", { persistent: false });
    };
  }, [mixpanelInstance, pathname, customerId, enabled]);

  useEffect(() => {
    if (sessionRecordingStartedRef.current) return;
    if (!mixpanelInstance) return;
    if (isPreview) return;
    if (!pathname) return;
    const isFullRecordingPath = FULL_SESSION_RECORDING_PATH_PREFIXES.some((prefix) =>
      pathname.startsWith(prefix)
    );
    if (!isFullRecordingPath) return;

    const { activeRole } = decodeJwt<TokenInfo>(accessToken) ?? ({} as TokenInfo);
    if (!isTrackableVisitor({ activeRole, country })) return;

    sessionRecordingStartedRef.current = true;
    mixpanelInstance.start_session_recording();
  }, [mixpanelInstance, pathname, accessToken, country, isPreview]);

  const queuedValue = useMemo<Mixpanel>(() => {
    // Keep the same proxy across SDK loading so mounted measurements continue.
    return new Proxy({} as Mixpanel, {
      get(_target, name: string) {
        return function (...args: unknown[]) {
          const mp = loadedMixpanelRef.current;
          if (mp) {
            return (mp as unknown as Record<string, (...a: unknown[]) => unknown>)[name](...args);
          }
          if (apiKey) {
            queueRef.current.push({ method: name, args });
          }
        };
      },
    });
  }, [apiKey]);
  // Existing consumers also use SDK objects such as people.set after loading.
  const value = mixpanelInstance ?? queuedValue;

  const engagement = useMemo(
    () =>
      enabled && apiKey && pathname
        ? { mixpanel: queuedValue, path: pathname, customerId: customerId ?? null }
        : null,
    [enabled, apiKey, queuedValue, pathname, customerId]
  );

  return (
    <MixpanelContext.Provider value={value}>
      <EngagementContext.Provider value={engagement}>
        <PageEngagement />
        {children}
      </EngagementContext.Provider>
    </MixpanelContext.Provider>
  );
};
